/**
 * CetusEd NextGen — Application Root Component
 *
 * Owns the top-level routing and auth boundary:
 *   - /login is PUBLIC (the login form).
 *   - EVERY other route is PROTECTED behind <ProtectedRoute> (⛔2 fail closed):
 *     an unauthenticated visit renders the login page, never protected content.
 *   - The authenticated landing (/) is a REAL view backed by GET /api/auth/me.
 *     It is AUTH-only: it reads the signed-in identity, not student data, so it
 *     does NOT depend on the data stack.
 *   - DATA routes (/workspace, /settings) are additionally wrapped in
 *     <DataStackBoundary> (⛔2): they lazy-init the encrypted data stack and,
 *     while it refuses (today it does), show an honest "not available yet"
 *     instead of any student data. Pre-auth UI never touches that stack.
 *
 * MIGRATION TARGET: Workspace shell + dashboard content router.
 * Monolith equivalent: S2 static shell + S25 dashboard router.
 *
 * ⚠ PHYSICAL TAB IDS (S2): TAB_PANEL_MAP = [0,1,4,2,6,3,5,7,8]. Workspace tabs
 *   will become React components keyed by logical tab in a later slice.
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SessionProvider } from './core/session/SessionProvider';
import { ProtectedRoute } from './ui/auth/ProtectedRoute';
import { LoginPage } from './ui/auth/LoginPage';
import { AuthenticatedHome } from './ui/auth/AuthenticatedHome';
import { Shell } from './ui/chrome/Shell';
import { DataStackBoundary } from './ui/data/DataStackBoundary';
import { Workspace } from './ui/screens/Workspace';
import { Settings } from './ui/screens/Settings';
import { MiraProvider } from './ui/mira/MiraProvider';

export function App() {
  return (
    <SessionProvider>
      <MiraProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Shell><AuthenticatedHome /></Shell>} />
              <Route
                path="/workspace/:studentId/*"
                element={<Shell><DataStackBoundary><Workspace /></DataStackBoundary></Shell>}
              />
              <Route
                path="/settings/*"
                element={<Shell><DataStackBoundary><Settings /></DataStackBoundary></Shell>}
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </MiraProvider>
    </SessionProvider>
  );
}
